<script setup>
import PartneroBranding from "../PartneroBranding.vue";
import {isCustomColor, isRoute} from "../../../helpers/common"
import {computed} from "vue";
import {usePortalContentAndDesignStore} from "../../../stores/partners/portalContentAndDesignStore";
import EditableTextEditorField from "../portal-editable-fields/EditableTextEditorField.vue";
import EditableTextField from "../portal-editable-fields/EditableTextField.vue";

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const getMaxWidthClass = computed(() => {
    const width = (portalContentAndDesignStore.signupPageSettings?.signup && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance)
        ? portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_width
        : portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_width;

    if (width === 'lg:w-8/12') return 'max-w-3xl';
    if (width === 'lg:w-6/12') return 'max-w-lg';
    return 'max-w-md';
});

const getTextColor = computed(() => {
    const color = isCustomColor(portalContentAndDesignStore.portalLayoutSettings.public_page_text_color) ? 'custom-text' : portalContentAndDesignStore.portalLayoutSettings.public_page_text_color;
    return 'text-' + color;
});

const shouldShowSidebar = computed(() => {
    if (portalContentAndDesignStore.signupPageSettings?.signup && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance) {
        return portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_side !== 'none';
    }
    return portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_side !== 'none';
});

const layout = computed(() => portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout)
const corners = computed(() => portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout_corners !== 'none'
    ? portalContentAndDesignStore.portalLayoutSettings.public_page_content_layout_corners
    : '')

const symbol = computed(() => portalContentAndDesignStore.srcRoutes.portal_layout_file_symbol)
const logo = computed(() => portalContentAndDesignStore.srcRoutes.portal_layout_file_logo)
const brandingType = computed(() => {
    if (isRoute('partners.login.show')) {
        return portalContentAndDesignStore.loginPageSettings?.program_login_page_branding_type;
    }
    if (isRoute('partners.register.show')) {
        return portalContentAndDesignStore.signupPageSettings?.signup?.program_signup_page_branding_type || 'none';
    }

    return 'none';
})

const shouldShowHeading = computed(() => {
    return [
        'partners.login.show',
        'partners.register.show',
        'partners.password.request.show',
        'partners.password.reset.show'
    ].includes(route().current());
})

const getHeadingName = computed(() => {
    if (isRoute('partners.login.show')) {
        return 'login_label';
    }

    if (isRoute('partners.register.show')) {
        return 'register_label';
    }

    if (isRoute('partners.password.request.show') || isRoute('partners.password.reset.show')) {
        return 'password_reset_label';
    }
});

const getPrimaryColor = computed(() => {
    const primaryColor = isCustomColor(portalContentAndDesignStore.portalLayoutSettings.portal_layout_brand_primary_color)
        ? 'custom-primary'
        : portalContentAndDesignStore.portalLayoutSettings.portal_layout_brand_primary_color;

    return 'bg-' + primaryColor;
});

const getBackgroundImage = computed(() => {
    return (portalContentAndDesignStore.signupPageSettings?.signup && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance)
        ? portalContentAndDesignStore.srcRoutes.portal_signup_file_background || 'https://app.partnero.com/images/bg.png'
        : portalContentAndDesignStore.srcRoutes.portal_login_file_background || 'https://app.partnero.com/images/bg.png';
});

const getHeadingStoreField = computed({
    get: () => {
        if (isRoute('partners.login.show')) {
            return portalContentAndDesignStore.translationSettings.portal_login_title
                ? portalContentAndDesignStore.translationSettings.portal_login_title
                : portalContentAndDesignStore.translationSettings.portal_translation_login_login_to;
        }

        if (isRoute('partners.register.show')) {
            return portalContentAndDesignStore.translationSettings.portal_signup_title
                ? portalContentAndDesignStore.translationSettings.portal_signup_title
                : portalContentAndDesignStore.translationSettings.portal_translation_signup_register_to;
        }

        if (isRoute('partners.password.request.show') || isRoute('partners.password.reset.show')) {
            return portalContentAndDesignStore.translationSettings.portal_translation_pass_reset_reset_password;
        }
    },
    set: (val) => {
        if (isRoute('partners.login.show')) {
            portalContentAndDesignStore.translationSettings.portal_login_title
                ? portalContentAndDesignStore.translationSettings.portal_login_title = val
                : portalContentAndDesignStore.translationSettings.portal_translation_login_login_to = val;
        } else if (isRoute('partners.register.show')) {
            portalContentAndDesignStore.translationSettings.portal_signup_title
                ? portalContentAndDesignStore.translationSettings.portal_signup_title = val
                : portalContentAndDesignStore.translationSettings.portal_translation_signup_register_to = val;
        } else if (isRoute('partners.password.request.show') || isRoute('partners.password.reset.show')) {
            portalContentAndDesignStore.translationSettings.portal_translation_pass_reset_reset_password = val;
        }
    }
});

const getSubheaderStoreField = computed({
    get: () => {
        if (isRoute('partners.login.show')) {
            return portalContentAndDesignStore.translationSettings.portal_login_description;
        }

        if (isRoute('partners.register.show')) {
            return portalContentAndDesignStore.translationSettings.portal_signup_description;
        }

        if (isRoute('partners.password.request.show') || isRoute('partners.password.reset.show')) {
            return portalContentAndDesignStore.translationSettings.portal_translation_pass_reset_back_to_login;
        }
    },
    set: (val) => {
        if (isRoute('partners.login.show')) {
            portalContentAndDesignStore.translationSettings.portal_login_description = val;
        } else if (isRoute('partners.register.show')) {
            portalContentAndDesignStore.translationSettings.portal_signup_description = val;
        } else if (isRoute('partners.password.request.show') || isRoute('partners.password.reset.show')) {
            portalContentAndDesignStore.translationSettings.portal_translation_pass_reset_back_to_login = val;
        }
    }
});

const subheaderExists = computed(() => {
    return !!getSubheaderStoreField.value
})

</script>

<template>
    <div
        v-if="portalContentAndDesignStore.portalInitialized"
        class="flex-1 h-screen flex overflow-hidden"
        :class="{ 'flex-row-reverse':
            (portalContentAndDesignStore.signupPageSettings?.signup && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance)
             ? portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_side === 'right'
             : portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_side === 'right'
        }"
        v-cloak
    >
        <div
            class="flex w-full"
            :class="(portalContentAndDesignStore.signupPageSettings?.signup && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance)
                ? (portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_side === 'none' ? 'flex-1' : portalContentAndDesignStore.portalLayoutSettings.portal_signup_sidebar_width)
                : (portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_side === 'none' ? 'flex-1' : portalContentAndDesignStore.portalLayoutSettings.portal_login_sidebar_width)"
        >
            <div
                :class="[
                    'flex flex-1 lg:px-20 px-6 xl:px-24 py-12 overflow-y-auto',
                    portalContentAndDesignStore.getPageBackgroundColorClass,
                    portalContentAndDesignStore.getPageBackgroundGradientColorClass
                ]"
            >
                <div
                    class="m-auto w-full"
                    :class="getMaxWidthClass"
                >
                    <div
                        class="text-center space-y-2"
                        :class="getTextColor"
                    >
                        <slot name="subheader">
                            <div v-if="brandingType && brandingType !=='none'" class="flex justify-center py-3">
                                <template v-if="brandingType === 'symbol' && symbol">
                                    <div
                                        class="relative h-20 w-20 lg:h-32 lg:w-32 flex-shrink-0 bg-white bg-cover bg-center"
                                        :class="[layout === 'one' ? 'rounded' : '', corners]"
                                        :style="{ backgroundImage: `url(${symbol})` }"
                                    ></div>
                                </template>

                                <template v-else-if="brandingType === 'logo' && logo">
                                    <div class="relative w-auto" style="max-width: 256px;">
                                        <img :src="logo" class="w-auto h-auto" />
                                    </div>
                                </template>
                            </div>
                            <editable-text-field
                                v-if="shouldShowHeading"
                                :name="getHeadingName"
                                v-model="getHeadingStoreField"
                                text-type="h2"
                                :class="'text-3xl font-semibold' + getTextColor + ' ' + 'text-center'"
                            >
                                <template #custom-content>
                                    {{ getHeadingStoreField }}
                                    {{
                                        (portalContentAndDesignStore.translationSettings?.portal_login_title || portalContentAndDesignStore.translationSettings?.portal_signup_title)
                                            ? ''
                                            : (portalContentAndDesignStore.program.name ?? '')
                                    }}
                                </template>
                            </editable-text-field>

                            <!-- We don't want to update program name from the portal -->
                            <h2 v-else class="text-3xl font-semibold">
                              {{ portalContentAndDesignStore.program.name }}
                            </h2>

                            <editable-text-editor-field
                                v-if="subheaderExists && !isRoute('partners.password.request.show') && !isRoute('partners.password.reset.show')"
                                v-model="getSubheaderStoreField"
                                class="partnero-page-content-section opacity-75"
                            />
                            <editable-text-field
                                v-else-if="subheaderExists && (isRoute('partners.password.request.show') || isRoute('partners.password.reset.show'))"
                                v-model="getSubheaderStoreField"
                            >
                                <div class="text-center space-y-2 text-sm">
                                    <a :href="portalContentAndDesignStore.getLoginRoute"
                                       :class="[
                                           'hover:underline text-opacity-100',
                                            portalContentAndDesignStore.getLinkColorClass
                                       ]"
                                    >
                                        {{ getSubheaderStoreField }}
                                    </a>
                                </div>
                            </editable-text-field>
                        </slot>
                    </div>

                    <div class="mt-10">
                      <slot name="content"></slot>
                    </div>

                    <partnero-branding
                        :portal-general-partnero-branding-enabled="portalContentAndDesignStore.partneroBrandingSettings?.enabled ?? false"
                        :unique-key-link="portalContentAndDesignStore.partneroBrandingSettings?.unique_key_link ?? ''"
                    />
                </div>
            </div>
        </div>
        <div
            v-if="shouldShowSidebar"
            class="flex-1 hidden lg:flex relative"
            :class="getPrimaryColor"
        >
            <img
                class="absolute inset-0 h-full w-full object-cover"
                :src="getBackgroundImage"
                alt=""
            />
        </div>
    </div>

</template>
